import { getPlatform } from "./platform";
import brandingFailure from "@companion-core/shared/app/Assets/config/brandingFailure.json";
import configFailure from "@companion-core/shared/app/Assets/config/configFailure.json";
import { useAuthStore } from "@companion-core/shared/app/Store/authStore";
import { useConfigStore } from "@companion-core/shared/app/Store/configStore";
import { NavigationListObject } from "@companion-core/shared/app/Interfaces/navigation";
import { ConfigResponse } from "@companion-core/shared/app/Interfaces/config";
import { CONFIG_TYPE } from "@companion-core/shared/app/Constants/config";
import {
  fetchConfig,
  fetchPublicResource,
  getClientToken,
} from "@companion-core/shared/app/Utils/apiClient";
import * as configUtils from "./config";

export interface ApiEnvironment {
  configFileName?: string;
  brandingFileName?: string;
  apiUrl?: string;
  i18nUrl?: string;
  clientId?: string;
  clientSecret?: string;
  pianoAnalytics?: {
    site?: string;
    collectDomain?: string;
  };
}

let appEnvironment: string;
let env: ApiEnvironment | null = null;

export function setAppEnvironment(environment: string | undefined, apiEnv: ApiEnvironment) {
  appEnvironment = environment ?? "development";
  env = apiEnv;
}

export function getAppEnv() {
  return appEnvironment;
}

export function isProductionEnv() {
  return getAppEnv() !== "development";
}

export function getEnv() {
  return env;
}

export function getConfig() {
  return useConfigStore.getState().config;
}

export function getConfigFileName() {
  return useConfigStore.getState().publicConfig?.config_file_name ?? env?.configFileName;
}

export function getBrandingFileName() {
  return useConfigStore.getState().publicConfig?.branding_file_name ?? env?.brandingFileName;
}

export function getModule(module: string) {
  return useConfigStore.getState().config?.modules[module];
}

export function getClientCredentials() {
  return {
    clientId: env?.clientId ?? useConfigStore.getState().publicConfig?.data.client_id,
    clientSecret: env?.clientSecret ?? useConfigStore.getState().publicConfig?.data.client_secret,
  };
}

export function getApiURL() {
  return env?.apiUrl;
}

export function getI18nUrl() {
  return env?.i18nUrl ?? useConfigStore.getState().publicConfig?.data?.translation_slug;
}

export function getOptions() {
  return useConfigStore.getState().config?.options;
}

export function getCurrency() {
  return getOptions()?.currency;
}

export function getHomeRoutes() {
  const platform = getPlatform() || "web";
  return getOptions()?.homeRoutes[platform] ?? {};
}

export function getLocales() {
  return getOptions()?.locales;
}

// Blocks listed here paint their own full-screen background and apply the safe-area insets
// themselves, so their page and block containers must neither scroll nor pad. Kept as a
// fallback so a companion whose configuration predates the option still renders correctly.
export const DEFAULT_FULL_BLEED_BLOCK_TYPES = ["geolocation"];

export function getFullBleedBlockTypes(): string[] {
  return getOptions()?.fullBleedBlockTypes ?? DEFAULT_FULL_BLEED_BLOCK_TYPES;
}

export const getNavigationLists = () =>
  useAuthStore.getState().authenticated
    ? getModule("my-account")?.navigationLists?.logged
    : getModule("my-account")?.navigationLists?.unlogged;

/**
 * Retrieves navigation lists based on the authentication status of the user.
 * @param {Object} config - The configuration object containing modules and navigation lists.
 * @param {boolean} authenticated - Flag indicating whether the user is authenticated or not.
 * @returns {Object} The navigation list based on the authentication status.
 */
export const getNavigationListsBasedOnAuthenticationStatus = (
  config: Record<string, unknown> | null,
  authenticated: boolean
): NavigationListObject[] => {
  const modules = (config?.modules ?? {}) as Record<
    string,
    { navigationLists?: Record<string, unknown> }
  >;
  const navigationLists = modules["my-account"]?.navigationLists;
  return (
    authenticated ? navigationLists?.logged : navigationLists?.unlogged
  ) as NavigationListObject[];
};

/**
 * Updates the configuration and branding data in the config store based on the provided responses.
 * @param {ConfigResponse} configResponse - The response containing configuration data.
 * @param {ConfigResponse} brandingResponse - The response containing branding data.
 */
export const updateConfigStore = ({
  configResponse,
  brandingResponse,
}: {
  configResponse?: ConfigResponse;
  brandingResponse?: ConfigResponse;
}): void => {
  if (configResponse && configResponse.success) {
    useConfigStore.getState().setConfig(configResponse.config ?? configFailure);
    useConfigStore.getState().setFailure(false, CONFIG_TYPE.CONFIG);
  } else {
    useConfigStore.getState().setConfig(configFailure);
    useConfigStore.getState().setFailure(true, CONFIG_TYPE.CONFIG);
  }
  if (brandingResponse && brandingResponse.success) {
    useConfigStore.getState().setBranding(brandingResponse.config ?? brandingFailure);
    useConfigStore.getState().setFailure(false, CONFIG_TYPE.BRANDING);
  } else {
    useConfigStore.getState().setBranding(brandingFailure);
    useConfigStore.getState().setFailure(true, CONFIG_TYPE.BRANDING);
  }
};

/**
 * Function to handle the ejection of a member.
 * At this moment, interceptor is not set so we have to manually logout.
 * After logout, it fetches all resources, updates the configuration store with the received config and branding data.
 * @returns {Promise<void>} A Promise that resolves when the ejection process is completed.
 */
const onMemberEjected = async () => {
  // At this moment, interceptor is not set so we have to manually logout
  await useAuthStore.getState().logout(false, false);
  await getClientToken(false);
  await fetchAllResources(false);
};

/**
 * Fetch all resources including config and branding data asynchronously.
 *
 * @param {boolean} [checkMemberEjection=true] - Flag to indicate whether to check for member ejection.
 * @returns {Promise<void>} A promise that resolves when config and branding data resolved.
 */
export const fetchAllResources = async (checkMemberEjection = true): Promise<void> => {
  const configFileName = getConfigFileName();
  const brandingFileName = getBrandingFileName();
  await Promise.all<ConfigResponse>([
    fetchConfig(configFileName, CONFIG_TYPE.CONFIG),
    fetchConfig(brandingFileName, CONFIG_TYPE.BRANDING),
  ])
    .then(async ([configResponse, brandingResponse]) => {
      if (
        checkMemberEjection &&
        ([configResponse?.error?.status, brandingResponse?.error?.status].includes(401) ||
          [configResponse?.error?.status, brandingResponse?.error?.status].includes(403))
      ) {
        // If retrieving member config failed with 401 or 403,
        // the member will be ejected and redirected to login page so we try to load client config before
        await onMemberEjected();
      } else {
        configUtils.updateConfigStore({
          configResponse: configResponse as ConfigResponse,
          brandingResponse: brandingResponse as ConfigResponse,
        });
      }
    })
    .catch(() => {
      configUtils.updateConfigStore({
        configResponse: { success: false, config: configFailure } as ConfigResponse,
        brandingResponse: { success: false, config: brandingFailure } as ConfigResponse,
      });
    });
};

/**
 * Fetch the given forms and store them in the config store.
 *
 * @param {string[]} [formsToLoad] - A list containing the names of the resources to load.
 * @param {string} [platform] - The platform of the user's device.
 * @returns {Promise<void>} A promise that resolves when the forms data resolved.
 */
export const fetchForms = async (formsToLoad: string[], platform: string): Promise<void> => {
  const formsPromises = formsToLoad.map((slug: string) => fetchPublicResource(slug, platform));
  const responses = await Promise.all(formsPromises);
  responses.forEach((response) => {
    if (response?.success) useConfigStore.getState().addForm(response.slug, response?.data);
  });
};
